/**
 * Normalizadores wire → *Local para los endpoints `/platform/tenants/*`.
 *
 * El wire shape coincide con el contrato generado (`backoffice.gen.ts`); la única
 * transformación es epoch ms (number) → ISO string en los timestamps, para que la UI
 * renderice fechas sin tocar `Date` en cada componente. Siempre con defaults (nunca dejan
 * un campo requerido en `undefined`), igual que `tenantNormalizers.ts`.
 *
 * Se aplican dentro de `platformApi.*` (api/endpoints/platform.ts).
 */
import type {
    PlatformTenantListItemLocal,
    PlatformTenantDetailLocal,
    PlatformTenantCognitoPoolLocal,
    TenantDetailWire,
    TenantCognitoPoolWire,
} from './PlatformLocal';

interface PlatformTenantListWire {
    items?: TenantDetailWire[];
}

/** epoch ms (number) | nulo → ISO string (o '' si no hay dato). */
function toIso(value: number | null | undefined): string {
    if (value === undefined || value === null) return '';
    return new Date(value).toISOString();
}

function normalizeCognitoPool(wire: TenantCognitoPoolWire): PlatformTenantCognitoPoolLocal {
    return {
        userPoolId: wire.userPoolId,
        userPoolArn: wire.userPoolArn,
        region: wire.region,
        appClientId: wire.appClientId,
        displayName: wire.displayName,
        createdAt: toIso(wire.createdAt),
    };
}

export function normalizePlatformTenant(wire: TenantDetailWire): PlatformTenantListItemLocal {
    return {
        tenantId: wire.tenantId,
        displayName: wire.displayName,
        status: wire.status,
        tenantType: wire.tenantType,
        tokenValidationMode: wire.tokenValidationMode,
        externalScopeLevels: wire.externalScopeLevels,
        applicationId: wire.applicationId,
        levels: wire.levels,
        createdAt: toIso(wire.createdAt),
        updatedAt: toIso(wire.updatedAt),
    };
}

export function normalizePlatformTenantList(wire: PlatformTenantListWire | null | undefined): {
    items: PlatformTenantListItemLocal[];
} {
    const items = wire?.items ?? [];
    return { items: items.map(normalizePlatformTenant) };
}

export function normalizePlatformTenantDetail(wire: TenantDetailWire): PlatformTenantDetailLocal {
    return {
        tenantId: wire.tenantId,
        displayName: wire.displayName,
        tablePrefix: wire.tablePrefix,
        status: wire.status,
        // DEC-RBAC-079/081: identificador de login del tenant (pasan tal cual; el consumidor
        // aplica los defaults EMAIL/false para tenants viejos que no los traen).
        principalIdentifier: wire.principalIdentifier,
        allowSecondary: wire.allowSecondary,
        scope: wire.scope,
        legalInfo: wire.legalInfo,
        cognitoPool: normalizeCognitoPool(wire.cognitoPool),
        securityPolicy: wire.securityPolicy,
        brandingConfig: wire.brandingConfig,
        featureFlags: wire.featureFlags,
        connectorArns: wire.connectorArns,
        s3Bucket: wire.s3Bucket,
        iamRoleArn: wire.iamRoleArn,
        createdAt: toIso(wire.createdAt),
        updatedAt: toIso(wire.updatedAt),
        createdBy: wire.createdBy,
        updatedBy: wire.updatedBy,
        tenantType: wire.tenantType,
        externalScopeLevels: wire.externalScopeLevels,
        applicationId: wire.applicationId,
        levels: wire.levels,
    };
}
